import { Head, Link, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Props {
  evidence_type: {
    name: string
    media_kind: string | null
  }
  errors?: Record<string, string[]>
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Manage", href: "/admin/evidence_types" },
  { title: "Evidence Types", href: "/admin/evidence_types" },
  { title: "Add", href: "/admin/evidence_types/new" },
]

export default function New({ evidence_type, errors }: Props) {
  const { data, setData, post, processing, transform } = useForm({
    name: evidence_type.name,
    media_kind: evidence_type.media_kind ?? "video",
  })

  // Map the "None" selection ("") back to null so the new type doesn't roll up
  // to a V or P report icon.
  transform((data) => ({
    evidence_type: { ...data, media_kind: data.media_kind || null },
  }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post("/admin/evidence_types")
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Add Evidence Type" />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Add Type</h1>

        <form onSubmit={handleSubmit} className="max-w-md">
          <div className="mb-4">
            <label htmlFor="name" className="mb-1 block font-medium">
              Name
            </label>
            <input
              type="text"
              id="name"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={50}
            />
            {errors?.name && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.name.join(", ")}
              </p>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="media_kind" className="mb-1 block font-medium">
              Kind
            </label>
            <select
              id="media_kind"
              value={data.media_kind}
              onChange={(e) => setData("media_kind", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            >
              <option value="video">Video (V)</option>
              <option value="photo">Photo (P)</option>
              <option value="">None (no icon)</option>
            </select>
            <p className="text-hpi-med-gray mt-1 text-sm">
              Determines whether the final report shows a V or P icon for this
              evidence type. Choose None for types that should not roll up to
              either.
            </p>
            {errors?.media_kind && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.media_kind.join(", ")}
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              Add
            </button>
            <Link
              href="/admin/evidence_types"
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
